import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { NavigationService } from '../../services/navigation.service';
import { Category } from '../../types/event-response.type';
import { CountryType } from '../../types/country.type';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap/datepicker/ngb-date-struct';
import { SearchQueryParams } from '../../types/ui.type';
import { ActivatedRoute, Params } from '@angular/router';
import { map, Observable, Subscription } from 'rxjs';
import { BackdropService } from '../../services/backdrop.service';
import { SearchDateDropdownComponent } from './search-date-dropdown/search-date-dropdown.component';
import { SearchQueryDropdownComponent } from './search-query-dropdown/search-query-dropdown.component';
import { SearchLocationDropdownComponent } from './search-location-dropdown/search-location-dropdown.component';
import { ResizeService } from '../../services/resize.service';
import { unwrapCategoriesUtil } from '../../utils/unwrapCategories.util';

type DropdownType = 'query' | 'date' | 'location';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchComponent implements OnDestroy {
  @ViewChild('desktopQuery', { static: false })
  desktopQuery: SearchQueryDropdownComponent | null = null;

  @ViewChild('mobileQuery', { static: false })
  mobileQuery: SearchQueryDropdownComponent | null = null;

  @ViewChild('mobileDate', { static: false })
  mobileDate: SearchDateDropdownComponent | null = null;

  @ViewChild('mobileLocation', { static: false })
  mobileLocation: SearchLocationDropdownComponent | null = null;

  public categories$: Observable<Category[]> = this.route.data.pipe(
    map((data) => data['categories'])
  );

  public isMobile$ = this.resizeService.isMobile$;

  public containerWidth: number;
  public isOpen: boolean = false;

  private _mobileVersionOpen: boolean = false;
  private subscriptions: Subscription = new Subscription();

  get mobileVersionOpen(): boolean {
    return this._mobileVersionOpen;
  }

  set mobileVersionOpen(value: boolean) {
    if (value) {
      this.backdropService.setOverlay('backdrop-dark', true);
    } else {
      this.backdropService.setOverlay(null);
    }
    this._mobileVersionOpen = value;
  }

  public openDropdowns: DropdownType[] = [];

  private defaultParams: SearchQueryParams = {
    categories: [],
    locations: [],
    search: '',
  };

  private params: SearchQueryParams = {
    ...this.defaultParams,
    ...this.route.snapshot.queryParams,
  };

  get allCategories(): Category[] {
    return unwrapCategoriesUtil(this.route.snapshot.data['categories'] ?? []);
  }

  constructor(
    private navigationService: NavigationService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private backdropService: BackdropService,
    private resizeService: ResizeService
  ) {
    this.subscriptions.add(
      this.route.queryParams.subscribe((queryParams: Params) => {
        this.params = {
          ...this.defaultParams,
          ...queryParams,
        };

        this.desktopQuery?.clearAll();
        const queryCategories: number[] = Array.isArray(
          queryParams['categories']
        )
          ? queryParams['categories'].map(Number)
          : [Number(queryParams['categories'])];

        const selectedCategories = this.allCategories.filter((category) =>
          queryCategories.includes(category.category_id)
        );

        this.onCategorySelectionChange(selectedCategories);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get isDropdownClearable(): boolean {
    if (!this.isOpen) {
      return false;
    }

    const isCategorySelected = Array.isArray(this.params.categories)
      ? this.params.categories.length > 0
      : !!this.params.categories;
    const isLocationSelected = Array.isArray(this.params.locations)
      ? this.params.locations.length > 0
      : !!this.params.locations;
    const isSearchTerm = !!this.params.search?.trim();
    const isDateSelected = !!this.params.dateFrom || !!this.params.dateTo;

    return (
      isCategorySelected || isLocationSelected || isSearchTerm || isDateSelected
    );
  }

  goToResults(): void {
    this.navigationService.goToResults({
      ...this.route.snapshot.queryParams,
      ...this.params,
    });

    this.backdropService.setOverlay(null);
    this.mobileVersionOpen = false;
  }

  onCategorySelectionChange(categories: Category[]): void {
    this.params.categories = categories.map(({ category_id }) => category_id);
  }

  onSearchTermChange(searchTerm: string): void {
    this.params.search = searchTerm;
  }

  onLocationChange(locations: CountryType[]): void {
    this.params.locations = locations.map(({ country_id }) => country_id);
  }

  onDateChange([dateFrom, dateTo]: [
    NgbDateStruct | null,
    NgbDateStruct | null
  ]): void {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };
    this.params.dateFrom = dateFrom
      ? new Date(
          dateFrom.year,
          dateFrom.month - 1,
          dateFrom.day
        ).toLocaleDateString('fr-CA', options)
      : undefined;

    this.params.dateTo = dateTo
      ? new Date(dateTo.year, dateTo.month - 1, dateTo.day).toLocaleDateString(
          'fr-CA',
          options
        )
      : undefined;
  }

  onContainerClick(isMobile: boolean): void {
    if (isMobile) {
      setTimeout(() => {
        this.mobileVersionOpen = true;
        this.cdr.detectChanges();
      });
    }
  }

  onOpenChange(isOpen: boolean, type: DropdownType, isMobile: boolean): void {
    this.isOpen = isOpen;

    if (isOpen && !this.openDropdowns.includes(type)) {
      this.openDropdowns.push(type);
    }

    if (!isOpen && this.openDropdowns.includes(type)) {
      this.openDropdowns = this.openDropdowns.filter(
        (dropdown) => dropdown !== type
      );
    }

    if (isMobile) {
      return;
    }

    if (this.openDropdowns.length > 0) {
      this.backdropService.setOverlay('backdrop-light');
    } else {
      this.backdropService.setOverlay(null);
    }
  }

  clearAll(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.mobileLocation?.clearAll();
    this.mobileQuery?.clearAll();
    this.mobileDate?.clearAll(false);
    setTimeout(() => {
      this.cdr.detectChanges();
    });
  }

  hasAnySelected(): boolean {
    return (
      this.openDropdowns.includes('location') ||
      this.openDropdowns.includes('query') ||
      this.openDropdowns.includes('date')
    );
  }

  closeAll(): void {
    this.openDropdowns = [];
    this.mobileQuery?.searchDropdown?.close();
    this.mobileLocation?.dropdown?.close();
    this.mobileDate?.dropdown?.close();
  }

  apply(): void {
    this.closeAll();
  }

  clear(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();

    if (this.openDropdowns.includes('location')) {
      this.mobileLocation?.clearAll();
      this.mobileLocation?.dropdown?.open();
      return;
    }

    if (this.openDropdowns.includes('query')) {
      this.mobileQuery?.clearAll();
      this.mobileQuery?.searchDropdown?.open();
      return;
    }

    if (this.openDropdowns.includes('date')) {
      this.mobileDate?.clearAll();
      setTimeout(() => {
        this.mobileDate?.dropdown?.open();
      });
      return;
    }

    this.cdr.detectChanges();
  }

  onClickOutside(isMobile: boolean): void {
    if (isMobile && this.mobileVersionOpen) {
      this.mobileVersionOpen = false;
      this.cdr.detectChanges();
    }
  }
}
